import React, { useState, useRef, useMemo } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Divider,
  useTheme,
  Chip,
  List,
  alpha,
  ListItem,
  ListItemText,
  ListItemIcon,
   Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Badge,
} from '@mui/material';
import AWS from 'aws-sdk';
import { useIntl } from 'react-intl';
import HistoryIcon from '@mui/icons-material/History';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import { styled } from '@mui/material/styles';
import SendIcon from '@mui/icons-material/Send';
import BugReportIcon from '@mui/icons-material/BugReport';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import TaskIcon from '@mui/icons-material/Task';
import SecurityIcon from '@mui/icons-material/Security';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SpeedIcon from '@mui/icons-material/Speed';
import AssignmentIcon from '@mui/icons-material/Assignment';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import axios from 'axios';
import { useAuthToken } from "../TokenContext";
import { useSelector } from "react-redux";
import ExportOptions from './ExportOptions';
import { useCredentials } from '../useCredentials';

const MessageContainer = styled(Box)(({ theme }) => ({
  margin: 'auto',
  flexGrow: 1,
  overflow: 'auto',
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  height: 'calc(100vh - 250px)',
  '&::-webkit-scrollbar': {
    width: '0.4em'
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent'
  },
  '&::-webkit-scrollbar-thumb': {
    background: theme.palette.mode === 'dark' 
      ? alpha(theme.palette.common.white, 0.2)
      : theme.palette.grey[300],
    borderRadius: '4px'
  }
}));

const UserMessage = styled(Card)(({ theme }) => ({
  maxWidth: '80%',
  marginLeft: 'auto',
  marginBottom: theme.spacing(2),
  backgroundColor: theme.palette.mode === 'dark' 
    ? alpha(theme.palette.primary.main, 0.2)
    : theme.palette.primary.light,
  color: theme.palette.mode === 'dark'
    ? theme.palette.primary.light
    : theme.palette.primary.contrastText
}));

const BotMessage = styled(Card)(({ theme }) => ({
  maxWidth: '80%',
  marginRight: 'auto',
  marginBottom: theme.spacing(2),
  backgroundColor: theme.palette.mode === 'dark'
    ? alpha(theme.palette.background.paper, 0.5)
    : theme.palette.background.paper
}));

const TaskSection = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark'
    ? alpha(theme.palette.background.paper, 0.3)
    : theme.palette.grey[50],
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2)
}));

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

// Fixed Message component with proper jiraStories handling
const Message = ({ message }) => {
  
  // Universal content parser that handles any response format
  const parseContent = (content) => {
    const lines = content.split('\n').map(line => line.trim()).filter(line => line);
    const sections = [];
    let currentSection = null;
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      
      // Handle bullet points (• prefix)
      if (line.startsWith('•')) {
        if (currentSection) {
          sections.push(currentSection);
        }
        currentSection = {
          title: line.replace('•', '').trim().replace(':', ''),
          content: [],
          type: 'section'
        };
      }
      // Handle blockers
      else if (line.includes('blocker:')) {
        if (currentSection) {
          sections.push(currentSection);
        }
        currentSection = {
          title: 'Blocker',
          content: [line],
          type: 'blocker'
        };
      }
      // Handle section headers (lines ending with :)
      else if (line.endsWith(':') && !line.includes('blocker:') && !currentSection) {
        currentSection = {
          title: line.replace(':', ''),
          content: [],
          type: 'section'
        };
      }
      // Add content to current section
      else if (currentSection) {
        currentSection.content.push(line);
      }
      // Handle standalone content
      else {
        sections.push({
          title: '',
          content: [line],
          type: 'text'
        });
      }
    }
    
    if (currentSection) {
      sections.push(currentSection);
    }
    
    return sections;
  };

  const renderBlocker = (content) => {
    const line = content[0];
    const blockerText = line.split('blocker:')[1]?.split('reported_date:')[0] || 'Unknown blocker';
    const reportedDate = line.split('reported_date:')[1]?.split('talent_name:')[0] || 'Unknown date';
    const talentName = line.split('talent_name:')[1] || 'Unknown talent';

    return (
      <Box sx={{ 
        bgcolor: 'error.light', 
        p: 2, 
        borderRadius: 1,
        mb: 2 
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <BugReportIcon color="error" sx={{ mr: 1 }} />
          <Typography color="error.dark" fontWeight="bold">
            {blockerText}
          </Typography>
        </Box>
        <Box sx={{ ml: 4 }}>
          <Typography variant="body2" color="error.dark">
            Reported: {reportedDate}
          </Typography>
          <Typography variant="body2" color="error.dark">
            Talent: {talentName}
          </Typography>
        </Box>
      </Box>
    );
  };

  const renderVelocityChip = (title, value) => {
    const numericValue = value.join('').match(/[\d.]+/)?.[0];
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <SpeedIcon color="info" />
        <Typography variant="subtitle1" fontWeight="bold">
          {title}:
        </Typography>
        <Chip
          label={numericValue || value.join(' ')}
          color={title.includes('Created') ? 'success' : 'info'}
          variant="outlined"
          size="large"
        />
      </Box>
    );
  };

  const renderTextContent = (content) => {
    return content.map((line, index) => {
      // Handle markdown-style bold text
      if (line.includes('**')) {
        const parts = line.split('**');
        return (
          <Typography key={index} variant="body2" paragraph>
            {parts.map((part, partIndex) => 
              partIndex % 2 === 1 ? 
                <strong key={partIndex}>{part}</strong> : 
                part
            )}
          </Typography>
        );
      }
      
      // Handle numbered lists
      if (line.match(/^\d+\./)) {
        return (
          <Typography key={index} variant="body2" sx={{ ml: 2, mb: 0.5 }}>
            {line}
          </Typography>
        );
      }
      
      // Handle bullet points
      if (line.startsWith('-')) {
        return (
          <ListItem key={index} sx={{ py: 0, px: 0 }}>
            <ListItemIcon sx={{ minWidth: 20 }}>
              <TaskIcon color="primary" fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={line.replace(/^-/, '').trim()}
              primaryTypographyProps={{ variant: 'body2' }}
            />
          </ListItem>
        );
      }
      
      // Handle regular paragraphs
      if (line.trim()) {
        return (
          <Typography key={index} variant="body2" paragraph>
            {line}
          </Typography>
        );
      }
      
      return null;
    });
  };

  const getIconForSection = (title) => {
    const titleLower = title.toLowerCase();
    if (titleLower.includes('velocity')) return <SpeedIcon />;
    if (titleLower.includes('summary')) return <AssignmentIcon />;
    if (titleLower.includes('sprint')) return <TrendingUpIcon />;
    if (titleLower.includes('test')) return <CheckCircleIcon />;
    if (titleLower.includes('security')) return <SecurityIcon />;
    if (titleLower.includes('acceptance')) return <TaskIcon />;
    return <DescriptionIcon />;
  };

  const renderSection = (section) => {
    const { title, content, type } = section;

    if (type === 'blocker') {
      return renderBlocker(content);
    }

    if (title.toLowerCase().includes('velocity')) {
      return renderVelocityChip(title, content);
    }

    if (!title && type === 'text') {
      return (
        <Box sx={{ mb: 2 }}>
          {renderTextContent(content)}
        </Box>
      );
    }

    return (
      <TaskSection>
        {title && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            {getIconForSection(title)}
            <Typography variant="h6" sx={{ ml: 1 }} color="primary">
              {title}
            </Typography>
          </Box>
        )}
        
        <Box>
          {renderTextContent(content)}
        </Box>

        {/* Handle special cases for task sections */}
        {title.toLowerCase().includes('story points') && (
          <Chip
            label={`${content.join(' ')}`}
            color="primary"
            size="small"
            sx={{ mt: 1 }}
          />
        )}
      </TaskSection>
    );
  };

  const renderUniversalContent = (insight) => {
    const sections = parseContent(insight);
    
    return (
      <Box>
        {sections.map((section, index) => (
          <Box key={index}>
            {renderSection(section)}
          </Box>
        ))}
      </Box>
    );
  };

  // Helper function to safely process jiraStories data
  const processJiraStories = (jiraStories) => {
    if (!jiraStories) return { type: 'list', data: [] };
    
    // If it's already an array, return it as a simple list
    if (Array.isArray(jiraStories)) {
      return { type: 'list', data: jiraStories };
    }
    
    // If it's a string, analyze its structure
    if (typeof jiraStories === 'string') {
      // Check if it contains structured information with bullet points
      if (jiraStories.includes('•') || jiraStories.includes('\u2022')) {
        return { type: 'structured', data: jiraStories };
      }
      
      // Try to parse as JSON first
      try {
        const parsed = JSON.parse(jiraStories);
        if (Array.isArray(parsed)) {
          return { type: 'list', data: parsed };
        }
        // If parsed but not array, treat as structured
        return { type: 'structured', data: jiraStories };
      } catch (e) {
        // If not JSON, check for line breaks
        if (jiraStories.includes('\n')) {
          const lines = jiraStories.split('\n').filter(line => line.trim());
          // If multiple lines, treat as structured
          if (lines.length > 1) {
            return { type: 'structured', data: jiraStories };
          }
        }
        // Single line, treat as simple list
        return { type: 'list', data: [jiraStories] };
      }
    }
    
    // If it's an object, try to extract meaningful data
    if (typeof jiraStories === 'object') {
      // If it has a results or stories property
      if (jiraStories.results && Array.isArray(jiraStories.results)) {
        return { type: 'list', data: jiraStories.results };
      }
      if (jiraStories.stories && Array.isArray(jiraStories.stories)) {
        return { type: 'list', data: jiraStories.stories };
      }
      // Convert object to string representation
      return { type: 'structured', data: JSON.stringify(jiraStories, null, 2) };
    }
    
    // Fallback: wrap in array
    return { type: 'list', data: [String(jiraStories)] };
  };

  // Helper function to render structured Jira response
  const renderStructuredJiraResponse = (content) => {
    const lines = content.split('\n').filter(line => line.trim());
    
    return (
      <Box>
        {lines.map((line, index) => {
          const trimmedLine = line.trim();
          
          // Handle bullet points with •
          if (trimmedLine.startsWith('•') || trimmedLine.startsWith('\u2022')) {
            const bulletContent = trimmedLine.replace(/^[•\u2022]\s*/, '');
            
            // Check if it's a key-value pair
            if (bulletContent.includes('=') || bulletContent.includes(':')) {
              const parts = bulletContent.split(/[=:]/);
              if (parts.length === 2) {
                return (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <CheckCircleIcon color="success" sx={{ mr: 1, fontSize: 16 }} />
                    <Typography variant="body2">
                      <strong>{parts[0].trim()}:</strong> {parts[1].trim()}
                    </Typography>
                  </Box>
                );
              }
            }
            
            // Regular bullet point
            return (
              <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <CheckCircleIcon color="success"sx={{ 
                      mr: 1, 
                      fontSize: 16,
                      // Ensure icon is visible in dark mode
                      color: theme => theme.palette.mode === 'dark' 
                        ? theme.palette.success.light 
                        : theme.palette.success.main
                    }}  />
                <Typography variant="body2"sx={{
                      color: theme => theme.palette.mode === 'dark' 
                        ? theme.palette.text.primary 
                        : 'inherit'
                    }}>{bulletContent}</Typography>
              </Box>
            );
          }
          
          // Handle regular lines (headers or content)
          if (trimmedLine) {
            return (
              <Typography key={index} variant="body2" sx={{ 
                mb: 1, 
                fontWeight: trimmedLine.endsWith(':') ? 'bold' : 'normal',
                color: theme => theme.palette.mode === 'dark' 
                  ? theme.palette.text.primary 
                  : 'inherit'
              }}>
                {trimmedLine}
              </Typography>
            );
          }
          
          return null;
        })}
      </Box>
    );
  };

  if (message.type === 'user') {
    return (
      <UserMessage elevation={1}>
        <CardContent>
          <Typography>{message.text}</Typography>
        </CardContent>
      </UserMessage>
    );
  }

  return (
    <BotMessage elevation={1}>
      <CardContent>
        {message.text && (
          <Typography sx={{ mb: 2 }}>{message.text}</Typography>
        )}
        
        {message.insights && message.insights.map((insight, index) => (
          <Box key={index}>
            {renderUniversalContent(insight)}
          </Box>
        ))}
        
        {message.jiraStories && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Results submitted Successfully:
            </Typography>
            {(() => {
              const processed = processJiraStories(message.jiraStories);
              
              if (processed.type === 'structured') {
                // Render structured response with proper formatting
                return (
                  <Paper 
                    elevation={1} 
                     sx={{ 
    p: 2, 
    bgcolor: theme => theme.palette.mode === 'dark' 
      ? alpha(theme.palette.success.main, 0.15)  // Increased from 0.1
      : alpha(theme.palette.success.main, 0.05),
    border: theme => `1px solid ${alpha(theme.palette.success.main, 0.5)}`, // Increased from 0.3
    // Add text color for dark mode
    color: theme => theme.palette.mode === 'dark' 
      ? theme.palette.text.primary 
      : 'inherit'
  }}
                  >
                    {renderStructuredJiraResponse(processed.data)}
                  </Paper>
                );
              } else {
                // Render simple list format
                return (
                  <List dense>
                    {processed.data.map((story, index) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <DescriptionIcon color="success" sx={{
            color: theme => theme.palette.mode === 'dark' 
              ? theme.palette.success.light 
              : theme.palette.success.main
          }}/>
                        </ListItemIcon>
                        <ListItemText 
                          primary={story} 
                          primaryTypographyProps={{
                            style: { 
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word'
                            },sx: {
            color: theme => theme.palette.mode === 'dark' 
              ? theme.palette.text.primary 
              : 'inherit'
          }
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                );
              }
            })()}
          </Box>
        )}
      </CardContent>
    </BotMessage>
  );
};
const UploadStatusDialog = ({ 
  open, 
  onClose, 
  statusData, 
  filteredData,
  onRefresh, 
  isLoading, 
  dateFilter, 
  onDateFilterChange, 
  onClearDateFilter 
}) => {
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'complete':
        return 'success';
      case 'processing':
      case 'in_progress':
        return 'warning';
      case 'failed':
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: theme => theme.palette.mode === 'dark' 
            ? alpha(theme.palette.background.paper, 0.95)
            : theme.palette.background.paper
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center' 
      }}>
        <Typography variant="h6">File Upload Status</Typography>
        <Box>
          <Tooltip title="Refresh Status">
            <IconButton onClick={onRefresh} disabled={isLoading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        {/* Date Filter Section */}
        <Box sx={{ 
          mb: 3, 
          p: 2, 
          border: 1, 
          borderColor: 'divider', 
          borderRadius: 1,
          bgcolor: theme => theme.palette.mode === 'dark' 
            ? alpha(theme.palette.background.paper, 0.3)
            : alpha(theme.palette.primary.main, 0.05)
        }}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
            Filter by Date Range
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            alignItems: 'center', 
            flexWrap: 'wrap' 
          }}>
           <TextField
  label="Start Date"
  type="date"
  size="small"
  value={dateFilter.startDate || ''}
  onChange={(e) => onDateFilterChange('startDate', e.target.value)}
  InputLabelProps={{ shrink: true }}
  inputProps={{
    max: new Date().toISOString().split('T')[0] // Prevent future dates
  }}
  sx={{ 
    minWidth: 150,
    '& .MuiInputBase-input': {
      colorScheme: 'auto' // Better date picker styling
    }
  }}
/>
<TextField
  label="End Date"
  type="date"
  size="small"
  value={dateFilter.endDate || ''}
  onChange={(e) => onDateFilterChange('endDate', e.target.value)}
  InputLabelProps={{ shrink: true }}
  inputProps={{
    min: dateFilter.startDate || undefined,
    max: new Date().toISOString().split('T')[0]
  }}
  sx={{ 
    minWidth: 150,
    '& .MuiInputBase-input': {
      colorScheme: 'auto'
    }
  }}
/>
            <Button
              variant="outlined"
              size="small"
              onClick={onClearDateFilter}
              disabled={!dateFilter.startDate && !dateFilter.endDate}
            >
              Clear Filter
            </Button>
            <Box sx={{ ml: 'auto' }}>
              <Chip
                label={`Showing ${filteredData.length} of ${statusData.length} records`}
                color="primary"
                variant="outlined"
                size="small"
              />
            </Box>
          </Box>
        </Box>

        {filteredData.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">
              {statusData.length === 0 
                ? "No upload history found" 
                : "No records found for the selected date range"
              }
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>File Name</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Started At</TableCell>
                  <TableCell>Completed At</TableCell>
                  <TableCell>Duration</TableCell>
                  <TableCell>Error Message</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredData.map((item, index) => {
                  const startTime = item.started_at ? new Date(item.started_at) : null;
                  const endTime = item.completed_at ? new Date(item.completed_at) : null;
                  const duration = startTime && endTime ? 
                    Math.round((endTime - startTime) / (1000 * 60)) : null;

                  return (
                    <TableRow key={item.job_id || index}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {item.file_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ID: {item.job_id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={item.status}
                          color={getStatusColor(item.status)}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(item.started_at)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(item.completed_at)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {duration ? `${duration} min` : 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {item.error_message ? (
                          <Tooltip title={item.error_message}>
                            <Chip
                              label="Error"
                              color="error"
                              size="small"
                              variant="outlined"
                            />
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            None
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};
const PodAutomation = ({collapsed}) => {
  const { credentials, isLoaded, refresh } = useCredentials();
  const intl = useIntl();

  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [question, setQuestion] = useState('');
  const [podInsights, setPodInsights] = useState(null);
  const [jiraResponse, setJiraResponse] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);
  const theme = useTheme();
  const { user } = useSelector(state => state.auth);
  const token = useAuthToken();
const [uploadStatusData, setUploadStatusData] = useState([]);
const [filteredStatusData, setFilteredStatusData] = useState([]);
const [isStatusLoading, setIsStatusLoading] = useState(false);
const [statusDialogOpen, setStatusDialogOpen] = useState(false);
const [actionId, setActionId] = useState(null); // Add this line
const [dateFilter, setDateFilter] = useState({
  startDate: '',
  endDate: ''
});
  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      await handleFileUpload(file);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // S3 Configuration
  const S3Config = useMemo(() => ({
    bucketName: credentials.bucketName,
    region: credentials.region,
    accessKeyId: credentials.accessKeyId,
    secretAccessKey: credentials.secretAccessKey
  }), [credentials]);
  




  const fetchUploadStatus = async () => {
  setIsStatusLoading(true);
  try {
    if (!user?.data[0]?.emp_id) {
      throw new Error('Manager ID not found');
    }

    const response = await axios.post(
      process.env.REACT_APP_CLIENT_UPLAODSTATUSBYMANAGERID,
      {
        manager_id: user.data[0].emp_id
      },
      {
        headers: {
          Authorization: token
        }
      }
    );

    if (response.data.statusCode === 200) {
      const statusData = JSON.parse(response.data.body);
    setUploadStatusData(statusData);
setFilteredStatusData(statusData);
      setStatusDialogOpen(true);
    }
  } catch (error) {
    console.error('Error fetching upload status:', error);
    setErrorMessage('Error fetching upload status: ' + (error.response?.data?.message || error.message));
  } finally {
    setIsStatusLoading(false);
  }
};


const applyDateFilter = (startDate, endDate, data = uploadStatusData) => {
  if (!startDate && !endDate) {
    setFilteredStatusData(data);
    return;
  }

  const filtered = data.filter(item => {
    const itemDate = new Date(item.started_at);
    const start = startDate ? new Date(startDate) : null;
    const end = endDate ? new Date(endDate + 'T23:59:59') : null; // Include full end date
    
    if (start && end) {
      return itemDate >= start && itemDate <= end;
    } else if (start) {
      return itemDate >= start;
    } else if (end) {
      return itemDate <= end;
    }
    return true;
  });
  
  setFilteredStatusData(filtered);
};

const handleDateFilterChange = (field, value) => {
  const newDateFilter = { ...dateFilter, [field]: value };
  setDateFilter(newDateFilter);
  applyDateFilter(newDateFilter.startDate, newDateFilter.endDate);
};

const clearDateFilter = () => {
  setDateFilter({ startDate: '', endDate: '' });
  setFilteredStatusData(uploadStatusData);
};
  const s3 = new AWS.S3(S3Config);

  const handleFileUpload = async (file) => {
    setIsUploading(true);
    setErrorMessage(null);

    try {
      if (!user?.data[0]?.emp_id) {
        throw new Error('Manager ID not found');
      }

      // Initialize S3
      const s3 = new AWS.S3({
        region: credentials.region,
        accessKeyId: credentials.accessKeyId,
        secretAccessKey: credentials.secretAccessKey,
        signatureVersion: 'v4'
      });

      // Construct the file path using manager_id
      const managerId = user.data[0].emp_id;
      const key = `${managerId}/${Date.now()}-${file.name}`;

      const uploadParams = {
        Bucket: process.env.REACT_APP_BUCKETNAME,
        Key: key,
        Body: file,
        ContentType: file.type,
        ACL: 'public-read'
      };

      console.log('Starting S3 upload to path:', key);
      const s3UploadResult = await s3.upload(uploadParams).promise();
      console.log('S3 upload successful:', s3UploadResult);

      // Call your API with the upload result
      const apiResponse = await axios.post(process.env.REACT_APP_CLIENT_ORGFILEUPLOAD, {
        manager_id: managerId,
        file_name: file.name,
        org_code: "CLIENT#c4a7530851b09f06caef227637a2fbcd",
        file_location: s3UploadResult.Location
      }, { 
        headers: { 
          Authorization: token 
        }
      });

      setMessages(prev => [
        ...prev,
        { type: 'user', text: `Uploaded file: ${file.name}` },
        { type: 'bot', text: 'File uploaded successfully!' }
      ]);
      setUploadStatusData([]);
    } catch (error) {
      console.error('Upload error:', error);
      setErrorMessage('Error uploading file: ' + (error.response?.data?.message || error.message));
    } finally {
      setIsUploading(false);
    }
  };

  const handleSendClick = async () => {
    if (!question.trim()) return;
    
    setIsLoading(true);
    setErrorMessage(null);
    setPodInsights(null);
    setJiraResponse(null);

    try {
      const response = await axios.post(process.env.REACT_APP_CLIENT_PODAUTOMATION, {
        manager_id: user?.data[0]?.emp_id,
        query: question,
      });
      const responseData = response?.data?.pod_insights;
       const actionIdData = response?.data?.action_id
      setPodInsights(responseData);
setActionId(actionIdData); 
      if (responseData) {
        const insights = responseData.split('\n • ').slice(1);
        setMessages(prev => [
          ...prev,
          { type: 'user', text: question },
          { type: 'bot', insights: insights }
        ]);
        setQuestion('');
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setErrorMessage('Error fetching data: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateJiraStories = async () => {
    if (!podInsights) return;

    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      const response = await axios.post(process.env.REACT_APP_CLIENT_CREATINGJIRASTORY, {
        manager_id: user?.data[0]?.emp_id,
        pod_insights: podInsights,
         action_id: actionId
        
      });
      setJiraResponse(response.data.pod_automation);
      
      setMessages(prev => [
        ...prev,
        { type: 'bot', jiraStories: response.data.pod_automation }
      ]);
    } catch (error) {
      console.error('Error creating Jira stories:', error);
      setErrorMessage('Error creating Jira stories: ' + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendClick();
    }
  };

  return (
    <Container sx={{ 
      height: '90vh', 
      py: 3,
      flexGrow: 1,
      width: '100%',
      maxWidth: 'none',
      transition: 'all 0.3s ease',
    }}>
      <Paper elevation={3} sx={{ 
        height: '100%', 
        width: '100%',
        overflow: 'hidden', 
        display: 'flex', 
        flexDirection: 'column',
        bgcolor: theme => theme.palette.mode === 'dark' 
          ? alpha(theme.palette.background.paper, 0.8)
          : theme.palette.background.paper
      }}>
       <Box sx={{ 
  p: 2, 
  borderBottom: 1, 
  borderColor: 'divider', 
  display: 'flex', 
  justifyContent: 'space-between', 
  alignItems: 'center' 
}}>
  <Typography variant="h5" fontWeight="bold">
    {intl.formatMessage({ id: 'podAutomation' })}
  </Typography>
  <Box sx={{ display: 'flex', gap: 1 }}>
    <ExportOptions 
      messages={messages} 
      disabled={isLoading || isUploading || isSubmitting}
    />
    <Tooltip title="View Upload Status">
      <Badge 
        badgeContent={uploadStatusData.length > 0 ? uploadStatusData.length : 0}
        color="primary"
        variant={uploadStatusData.length > 0 ? "standard" : "dot"}
      >
        <Button
          variant="outlined"
          startIcon={<HistoryIcon />}
          onClick={fetchUploadStatus}
          disabled={isStatusLoading || isUploading}
          sx={{ minWidth: 'auto' }}
        >
          {isStatusLoading ? (
            <CircularProgress size={16} />
          ) : (
            'Status'
          )}
        </Button>
      </Badge>
    </Tooltip>
    <Button
      component="label"
      variant="contained"
      startIcon={<CloudUploadIcon />}
      disabled={isUploading}
    >
      {isUploading ? intl.formatMessage({ id: 'uploading' }) : intl.formatMessage({ id: 'uploadFile' })}
      <VisuallyHiddenInput 
        type="file" 
        onChange={handleFileChange}
        ref={fileInputRef}
        accept=".csv,.xlsx,.xls"
      />
    </Button>
  </Box>
</Box>


        <MessageContainer>
          {messages.map((message, index) => (
            <Message key={index} message={message} />
          ))}
          {(isLoading || isUploading) && (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
              <CircularProgress />
            </Box>
          )}
          {errorMessage && (
            <Alert severity="error" sx={{ mb: 2 }}>{errorMessage}</Alert>
          )}
        </MessageContainer>

        <Box sx={{ 
          p: 2, 
          borderTop: 1, 
          borderColor: 'divider', 
          backgroundColor: theme => theme.palette.mode === 'dark'
            ? alpha(theme.palette.background.paper, 0.9)
            : theme.palette.background.paper
        }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              multiline
              maxRows={4}
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={intl.formatMessage({ id: 'enterYourQuestionHere' })}
              variant="outlined"
              disabled={isLoading || isSubmitting}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: theme => theme.palette.mode === 'dark'
                    ? alpha(theme.palette.background.paper, 0.3)
                    : theme.palette.background.paper,
                  color: theme => theme.palette.mode === 'dark'
                    ? theme.palette.text.primary
                    : theme.palette.text.primary,
                },
                '& .MuiInputBase-input::placeholder': {
                  color: theme => theme.palette.mode === 'dark'
                    ? alpha(theme.palette.text.primary, 0.7)
                    : alpha(theme.palette.text.secondary, 0.7),
                  opacity: 1
                }
              }}
            />
            <Button
              variant="contained"
              onClick={handleSendClick}
              disabled={isLoading || isSubmitting || !question.trim()}
              endIcon={<SendIcon />}
              sx={{
                color: theme => theme.palette.mode === 'dark'
                  ? theme.palette.primary.contrastText
                  : theme.palette.primary.contrastText,
                bgcolor: theme => theme.palette.mode === 'dark'
                  ? theme.palette.primary.main
                  : theme.palette.primary.main
              }}
            >
              {intl.formatMessage({ id: 'send' })}
            </Button>
          </Box>
          
          {podInsights && !jiraResponse && (
            <Button
              variant="contained"
              color="success"
              onClick={handleCreateJiraStories}
              disabled={isSubmitting}
              sx={{ mt: 2 }}
            >
              {isSubmitting ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} color="inherit" />
                  {intl.formatMessage({ id: 'submitting' })}
                </>
              ) : intl.formatMessage({ id: 'submitTheResults' })}
            </Button>
          )}
        </Box>
 <UploadStatusDialog
  open={statusDialogOpen}
  onClose={() => setStatusDialogOpen(false)}
  statusData={uploadStatusData}
  filteredData={filteredStatusData}
  onRefresh={fetchUploadStatus}
  isLoading={isStatusLoading}
  dateFilter={dateFilter}
  onDateFilterChange={handleDateFilterChange}
  onClearDateFilter={clearDateFilter}
/></Paper>
    </Container>
  );
};

export default PodAutomation;